import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';

import { LocalTypeahead } from '@/components/common/local-typeahead';
import {
	ComposerAttachments,
	NO_ATTACHABLE,
	totalAttachmentBytes,
	type ComposerAttachmentItem,
} from '@/components/composer/composer-attachments';
import { useTheme } from '@/components/theme-provider';
import { ActivityIndicator } from '@/components/ui/activity-indicator';
import { Button } from '@/components/ui/button';
import { ComposerWindow } from '@/components/ui/composer-window';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { useComposerDraft } from '@/hooks/use-composer-draft';
import { useReplyPreview, useReplyToMessage } from '@/hooks/use-data';
import type { ReplyResult, ReplyTemplateSummary } from '@/services/data-service';
import { REVIEW_ACCENT } from '@/styles/theme';
import { formatRecipientList, hasRecipient, parseRecipientList, toggleRecipient } from '@/utils/recipient-list';

/**
 * Write back to the homeowner who sent this (#532).
 *
 * The case it exists for: an owner reports something that is the OWNER'S responsibility.
 * There is no work order to raise — someone has to say so, in writing, on the record.
 *
 * Modelled on `forward-modal.tsx`, and it keeps that file's load-bearing rule: a gated skip
 * must NEVER read as a success. Outside production the send is simulated, so the banner
 * says so while the sender is still typing rather than leaving them to infer it from a
 * toast after the fact.
 *
 * Two things this says that Forward does not. Replies come BACK here, not to the sender
 * personally — the whole point of the plus-addressed Reply-To — and the message is archived
 * on send by default, because answering "that's yours to fix" is the end of the work.
 *
 * It lives in a `ComposerWindow`, not a modal, for the reason Gmail does the same: answering
 * a message means re-reading it, and a backdrop put the message behind the box you were
 * typing into. The window drags aside, collapses to its title bar, and leaves the thread
 * underneath fully scrollable.
 */

export interface ReplyModalProps {
	messageId: string;
	visible: boolean;
	onDismiss: () => void;
	onSent: (result: ReplyResult) => void;
	onError: (error: unknown, fallback: string) => void;
	/** Open collapsed — used when `ComposerHost` restores a draft after a page reload. */
	startMinimized?: boolean;
	/** Bumped by the host to bring an already-open, collapsed composer back to the front. */
	expandRequest?: number;
	/** Lets the host warn before a click replaces or closes work in progress. */
	onDirtyChange?: (dirty: boolean) => void;
	/** The signed-in user id, from `ComposerHost`. Absent means do not persist a draft. */
	draftScope?: string | null;
}

/** Only the sender's own words are persisted; everything else is re-derived from the preview. */
interface ReplyDraft {
	to: string;
	subject: string;
	body: string;
	ccSelf: boolean;
	archiveAfterSending: boolean;
}

export const ReplyModal: React.FC<ReplyModalProps> = ({
	messageId, visible, onDismiss, onSent, onError, startMinimized, expandRequest, onDirtyChange, draftScope,
}) => {
	const theme = useTheme();
	const { data: preview, isLoading, error } = useReplyPreview(messageId, { enabled: visible });
	const reply = useReplyToMessage();

	const [to, setTo] = useState('');
	const [subject, setSubject] = useState('');
	const [body, setBody] = useState('');
	const [ccSelf, setCcSelf] = useState(false);
	const [archiveAfterSending, setArchiveAfterSending] = useState(true);
	/**
	 * Files on this reply (#603). Not in the saved draft: a staged upload expires within a day,
	 * so a draft restored tomorrow would hold references the send must refuse.
	 */
	const [attachments, setAttachments] = useState<ComposerAttachmentItem[]>([]);
	/** A file is still uploading or rendering — Send must wait for it. */
	const [attachmentsBusy, setAttachmentsBusy] = useState(false);
	const [dirty, setDirty] = useState(false);
	/** Which template the body currently came from — drives the picker's own label. */
	const [appliedTemplateId, setAppliedTemplateId] = useState<string | null>(null);

	/**
	 * What a blank reply starts as: room to type, then the sender's own signature (#564).
	 *
	 * In the editable box, not appended at send, so what goes out is exactly what the sender
	 * saw — and removing it is just deleting text. `sendReply` adds nothing behind it.
	 */
	const blankBody = preview?.signature ? `\n\n${preview.signature}` : '';

	/**
	 * Seed from the preview, but never clobber edits in progress — the preview refetches on
	 * focus and would otherwise wipe a half-written letter. Same guard as the needs-info
	 * modal, which learned it the same way.
	 *
	 * `visible` is in the deps for a reason. The reset effect below blanks every field on
	 * close, but leaves `dirty` at `false` — so on an untouched composer NEITHER dep changed
	 * and this never re-ran, and reopening gave a blank To and Subject. That was true before
	 * the composer window too; it only became easy to hit once Reply and Forward started
	 * closing each other, which turns "switch to Forward and back" into a reopen.
	 *
	 * The body is SET to the signature seed, never appended to, so a refetch or a reopen can
	 * re-run this any number of times and still hold one copy. A restored draft marks the
	 * composer dirty, which skips this — the sender's saved text already has whatever
	 * signature they left in it.
	 */
	useEffect(() => {
		if (!visible || !preview || dirty) return;
		// Everyone we know about, not just the first (#602) — the user's call. Safe because
		// `replyRecipients` runs EVERY candidate through `isReplyableSenderAddress`.
		setTo(formatRecipientList([preview.to ?? '', ...preview.recipients.map((person) => person.email)]));
		setSubject(preview.subject);
		setCcSelf(preview.ccSelf);
		setBody(blankBody);
	}, [visible, preview, dirty, blankBody]);

	/**
	 * Restore whatever was typed before the page was reloaded.
	 *
	 * Order against the preview seed above does not matter, which is deliberate. If this runs
	 * first it sets `dirty`, and the seed skips. If the seed runs first this overwrites it.
	 * Either way the sender's own words win over server-derived defaults.
	 */
	const draft = useComposerDraft<ReplyDraft>(draftScope, visible ? `reply:${messageId}` : null);
	const restoredRef = useRef<string | null>(null);
	useEffect(() => {
		if (!visible || !draft.ready || !draft.restored) return;
		// Once per message. Re-applying on a later render would fight live typing.
		if (restoredRef.current === messageId) return;
		restoredRef.current = messageId;
		setTo(draft.restored.to);
		setSubject(draft.restored.subject);
		setBody(draft.restored.body);
		setCcSelf(draft.restored.ccSelf);
		setArchiveAfterSending(draft.restored.archiveAfterSending);
		setDirty(true);
	}, [visible, messageId, draft.ready, draft.restored]);

	// Only once the sender has actually touched it — otherwise merely opening a composer and
	// closing it would leave a "draft" that is really just the server's own preview text.
	useEffect(() => {
		if (!visible || !dirty) return;
		draft.save({ to, subject, body, ccSelf, archiveAfterSending });
	}, [visible, dirty, to, subject, body, ccSelf, archiveAfterSending, draft]);

	// Same signal the draft save uses: the host asks before a click throws this away.
	// A file still being staged is unsaved work too: closing mid-upload would drop it without a word.
	useEffect(() => { onDirtyChange?.(visible && (dirty || attachmentsBusy)); }, [visible, dirty, attachmentsBusy, onDirtyChange]);

	// Reset per opening, so yesterday's letter is not pre-filled on a different message.
	useEffect(() => {
		if (visible) return;
		setTo('');
		setSubject('');
		setBody('');
		setArchiveAfterSending(true);
		setAttachments([]);
		setAttachmentsBusy(false);
		setAppliedTemplateId(null);
		setDirty(false);
		restoredRef.current = null;
	}, [visible]);

	const gate = preview?.emailGate;
	const willSend = gate?.willActuallySend ?? false;
	const busy = reply.isPending;
	// `preview.canSend` belongs here, not only on the button: Cmd+Enter reaches `submit`
	// directly, so a server-side blocker the button honours would otherwise be ignored by the
	// keyboard path. Latent while `api/messages-reply.ts` returns no blockers — which is
	// exactly how it would ship broken the day one is added.
	// A body that is still only the seeded signature has no message in it yet.
	const hasMessage = !!body.trim() && body.trim() !== blankBody.trim();
	// Over the email limit is refused server-side too; stopping here keeps the letter open.
	const attachmentsFit = totalAttachmentBytes(attachments) <= (preview?.attachable?.maxBytes ?? Infinity);
	const canSubmit = !busy && !attachmentsBusy && parseRecipientList(to).length > 0 && !!subject.trim() && hasMessage
		&& attachmentsFit
		&& (preview?.canSend ?? false);

	/**
	 * `null` resets to a blank reply rather than doing nothing.
	 *
	 * Without a way back, picking a template by mistake left its wording in the box with no
	 * undo but select-all-delete — and the subject silently keeping the template's wording
	 * even after the body was cleared by hand.
	 */
	const applyTemplate = (templateId: string | null) => {
		setDirty(true);
		if (!templateId) {
			setAppliedTemplateId(null);
			setSubject(preview?.subject ?? '');
			setBody(blankBody);
			return;
		}
		const template = preview?.templates.find((t) => t.id === templateId);
		if (!template) return;
		setAppliedTemplateId(templateId);
		setSubject(template.subject || subject);
		setBody(template.bodyText);
	};

	const applied = preview?.templates.find((t) => t.id === appliedTemplateId) ?? null;
	const appliedUnresolved = applied?.unresolvedTags ?? [];

	/**
	 * Picker rows: canned replies first, then the improvement letters.
	 *
	 * `description` carries both the group and the blank-tag warning, because the typeahead
	 * has no section headers — and a per-row note is the better fit regardless: it survives
	 * filtering, where a heading three rows up would have scrolled out of view.
	 */
	const templateOptions = useMemo(() => {
		const all = preview?.templates ?? [];
		const describe = (t: ReplyTemplateSummary): string => {
			const group = t.group === 'letter' ? 'Improvement letter' : 'Canned reply';
			if (!t.unresolvedTags.length) return group;
			return `${group} · ${t.unresolvedTags.length} tag${t.unresolvedTags.length === 1 ? '' : 's'} blank here`;
		};
		return [
			...all.filter((t) => t.group === 'reply'),
			...all.filter((t) => t.group === 'letter'),
		].map((t) => ({ id: t.id, label: t.name, description: describe(t) }));
	}, [preview?.templates]);

	const submit = async () => {
		if (!canSubmit) return;
		try {
			const result = await reply.mutateAsync({
				id: messageId,
				body: {
					to: parseRecipientList(to),
					subject: subject.trim(),
					bodyText: body.trim(),
					ccSelf,
					archiveAfterSending,
					attachments: attachments.map((item) => item.ref),
				},
			});
			// The letter is on the thread now; a stored draft would resurrect it next visit.
			draft.clear();
			onSent(result);
			onDismiss();
		} catch (e) {
			// Left open with the text intact — retyping a letter because of a 400 is miserable.
			// The draft also stays in storage, so even a crash here does not lose it.
			onError(e, 'Could not send this reply.');
		}
	};

	/** Pinned to the window's bottom edge, so Send survives any amount of scrolling. */
	const actions = preview && !isLoading && !error ? (
		<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
			{/* No backdrop-close on the composer window, so an explicit way out is required. */}
			<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
			<Button
				mode="contained"
				onPress={submit}
				disabled={!canSubmit}
				testID="btn-send-reply"
			>
				{busy ? 'Sending…' : willSend ? 'Send reply' : 'Record without emailing'}
			</Button>
		</View>
	) : undefined;

	return (
		<ComposerWindow
			visible={visible}
			onDismiss={onDismiss}
			onSubmit={submit}
			busy={busy}
			startMinimized={startMinimized}
			expandRequest={expandRequest}
			title="Reply to this message"
			subtitle="Their answer comes back here, onto this conversation — not to your own mailbox."
			footer={actions}
			testID="reply-composer"
		>
			{isLoading ? (
				<View style={{ padding: 32, alignItems: 'center' }}><ActivityIndicator /></View>
			) : error || !preview ? (
				<Text style={{ color: theme.colors.error }}>Could not prepare this reply.</Text>
			) : (
				<>
						{/* Stated BEFORE sending. "Recorded but not emailed" is the normal outcome
							 outside production, and learning that from a toast is learning it too late. */}
						{!willSend ? (
							<View
								testID="reply-gate-banner"
								style={{
									flexDirection: 'row', alignItems: 'flex-start',
									backgroundColor: theme.colors.errorContainer,
									borderRadius: 8, padding: 10, marginBottom: 12,
								}}
							>
								<Ionicons name="warning" size={16} color={theme.colors.error} style={{ marginRight: 8, marginTop: 1 }} />
								<Text style={{ flex: 1, minWidth: 0, fontSize: 12, color: theme.colors.onErrorContainer }}>
									This will be recorded on the thread but NOT emailed. {gate?.reason}
								</Text>
							</View>
						) : null}

						{/*
							A missing recipient is a NOTE now, not a blocker.

							It used to be the latter, on the reasoning that `from_address` is
							Webflow's relay on a website submission so there is often no real
							address anywhere — better to say where to add one than to let someone
							type into a void. That was right about form submissions and wrong about
							everything else: a homeowner who emails the office directly has a
							perfectly good address in `from_address` (now offered as a `sender`
							candidate), and the blocker DISABLED Send even after a PM typed a valid
							address by hand. A gate the user cannot satisfy from the surface showing
							it is not a gate, it is a dead end.
						*/}
						{preview.blockers.length ? (
							<View testID="reply-blockers" style={{ marginBottom: 12 }}>
								{preview.blockers.map((blocker) => (
									<Text key={blocker} style={{ color: theme.colors.error, fontSize: 12 }}>• {blocker}</Text>
								))}
							</View>
						) : null}

						{preview.notes?.length ? (
							<View testID="reply-notes" style={{ marginBottom: 12 }}>
								{preview.notes.map((note) => (
									<View key={note} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2 }}>
										<Ionicons
											name="information-circle-outline"
											size={14}
											color={theme.colors.onSurfaceVariant}
											style={{ marginRight: 6, marginTop: 1 }}
										/>
										<Text style={{ flex: 1, minWidth: 0, color: theme.colors.onSurfaceVariant, fontSize: 12 }}>
											{note}
										</Text>
									</View>
								))}
							</View>
						) : null}

						<Text style={{ fontSize: 12, fontWeight: '600', marginBottom: 4 }}>To</Text>
							<TextInput
								value={to}
								onChangeText={(v) => { setTo(v); setDirty(true); }}
								placeholder="owner@example.com"
								autoCapitalize="none"
								testID="input-reply-to"
							/>

							{/*
								One tap per person we know about. The form has a single email box, so
								only the first owner is ever given it — the others are reachable only
								from the lot, and typing an address by hand is how you reply to the
								wrong Smith. Each chip TOGGLES its address (#602): co-owners can both be
								answered, and anything typed by hand stays put.
							*/}
							{preview.recipients.length > 1 ? (
								<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
									{preview.recipients.map((person) => (
										<Pressable
											key={person.email}
											onPress={() => { setTo((current) => toggleRecipient(current, person.email)); setDirty(true); }}
											// `role`/`aria-*`, not `accessibilityState`: RN-Web 0.21 drops the
											// latter, so the checked state never reached the DOM.
											role="checkbox"
											aria-checked={hasRecipient(to, person.email)}
											testID={`reply-to-${person.email}`}
											style={{
												paddingVertical: 4, paddingHorizontal: 8, borderRadius: 12,
												borderWidth: 1,
												borderColor: hasRecipient(to, person.email)
													? theme.colors.primary
													: theme.colors.outlineVariant ?? theme.colors.outline,
											}}
										>
											{/*
												Label the non-obvious sources. "owner" says this is the
												person on the deed rather than whoever filled in the form;
												"sent this" says the address came off the message itself,
												which is worth distinguishing from a record we hold.
											*/}
											<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant }}>
												{person.name}
												{person.source === 'owner' ? ' · owner' : ''}
												{person.source === 'sender' ? ' · sent this' : ''}
											</Text>
										</Pressable>
									))}
								</View>
							) : null}

							{/*
								`LocalTypeahead` — the house dropdown, whose own docstring says to
								use it "anywhere a select would otherwise list more than ~5 options".

								Chips were fine for one canned reply and stop scaling the moment the
								office adds a few; this list now also carries the improvement letters.
								An earlier pass reached for `ActionMenu`, which was simply the wrong
								control: it is an ACTIONS menu (link styling, its own chevron) and has
								no notion of a selected value, so it read as a stray button rather
								than a field.

								The group and the blank-tag warning ride in each row's
								`description` — the typeahead has no section headers, and a
								per-row label is better anyway: it stays visible while filtering,
								when a heading several rows up would have scrolled away.
							*/}
							{preview.templates.length ? (
								<View style={{ marginTop: 14 }} testID="reply-templates">
									<LocalTypeahead
										label="Start from"
										placeholder="— Blank reply —"
										testID="reply-template-picker"
										items={templateOptions}
										toOption={(t) => t}
										value={appliedTemplateId}
										onChange={applyTemplate}
									/>
									{/*
										Named, not just flagged. "⚠ some tags are blank" sends someone
										hunting; naming them says exactly what to fix before sending.
									*/}
									{appliedUnresolved.length ? (
										<Text
											testID="reply-template-unresolved"
											style={{ color: REVIEW_ACCENT, fontSize: 11, marginTop: 6 }}
										>
											This letter was written for an improvement request, so{' '}
											{appliedUnresolved.map((t) => `{{${t}}}`).join(', ')}{' '}
											{appliedUnresolved.length === 1 ? 'is' : 'are'} blank here — edit before sending.
										</Text>
									) : null}
								</View>
							) : null}

							<Text style={{ fontSize: 12, fontWeight: '600', marginTop: 14, marginBottom: 4 }}>Subject</Text>
							<TextInput
								value={subject}
								onChangeText={(v) => { setSubject(v); setDirty(true); }}
								testID="input-reply-subject"
							/>

							<Text style={{ fontSize: 12, fontWeight: '600', marginTop: 14, marginBottom: 4 }}>Message</Text>
							<TextInput
								value={body}
								onChangeText={(v) => { setBody(v); setDirty(true); }}
								placeholder="Thanks for letting us know…"
								multiline
								numberOfLines={12}
								style={{ minHeight: 200 }}
								testID="input-reply-body"
							/>
							<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, marginTop: 6 }}>
								The original message is quoted underneath automatically.
							</Text>

							<ComposerAttachments
								target={{ kind: 'message', id: messageId }}
								attachable={preview.attachable ?? NO_ATTACHABLE}
								items={attachments}
								onChange={(update) => { setAttachments(update); setDirty(true); }}
								onError={onError}
								onBusyChange={setAttachmentsBusy}
								disabled={busy}
							/>

							<Pressable
								onPress={() => setCcSelf((prev) => !prev)}
								testID="btn-toggle-reply-cc"
								style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, marginTop: 8 }}
							>
								<Ionicons
									name={ccSelf ? 'checkbox' : 'square-outline'}
									size={16}
									color={ccSelf ? theme.colors.primary : theme.colors.onSurfaceVariant}
									style={{ marginRight: 8 }}
								/>
								<Text style={{ fontSize: 13 }}>Copy me on this reply</Text>
							</Pressable>

							<Pressable
								onPress={() => setArchiveAfterSending((prev) => !prev)}
								testID="btn-toggle-reply-archive"
								style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8 }}
							>
								<Ionicons
									name={archiveAfterSending ? 'checkbox' : 'square-outline'}
									size={16}
									color={archiveAfterSending ? theme.colors.primary : theme.colors.onSurfaceVariant}
									style={{ marginRight: 8 }}
								/>
								<Text style={{ fontSize: 13 }}>Archive this message after sending</Text>
							</Pressable>
							{/*
								Said plainly, because unticking it is the RIGHT answer whenever the
								reply is "we're sending someone out" — that message still needs a work
								order and must not leave the queue.
							*/}
							<Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant, paddingLeft: 24 }}>
								{archiveAfterSending
									? 'Untick this if the message still needs a work order.'
									: 'It stays in the inbox. Use Move to… → Waiting on customer if you are expecting an answer.'}
						</Text>
					</>
			)}
		</ComposerWindow>
	);
};

export default ReplyModal;
